import React, { useEffect, useState, useCallback } from "react";
import { api, fmtNum, fmtEur, fmtMonat, errMsg } from "@/lib/api";
import { MonthPicker } from "@/components/app/MonthPicker";
import { EmptyState } from "@/components/app/EmptyState";
import { QualityBadge } from "@/components/app/QualityBadge";
import { AbrechnungDetailDialog } from "@/pages/AbrechnungenPage";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Calculator, Loader2, Eye, CheckCircle2, XCircle, AlertCircle,
  Ban, Trash2, ClipboardList,
} from "lucide-react";
import { toast } from "sonner";

const vormonat = () => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const STATUS = {
  entwurf: { label: "Entwurf", variant: "secondary" },
  freigegeben: { label: "Freigegeben", className: "bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))] hover:bg-[hsl(var(--success))]" },
  storniert: { label: "Storniert", variant: "outline", className: "line-through" },
  gegenbuchung: { label: "Storno-Gegenbuchung", variant: "outline" },
};
const StatusBadge = ({ status }) => {
  const s = STATUS[status] || { label: status, variant: "secondary" };
  return <Badge variant={s.variant} className={s.className} data-testid={`status-badge-${status}`}>{s.label}</Badge>;
};

const StornoDialog = ({ settlement, onClose, onSaved }) => {
  const [kommentar, setKommentar] = useState("");
  const [neu, setNeu] = useState(true);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setKommentar(""); setNeu(true); }, [settlement]);
  if (!settlement) return null;
  const stornieren = async () => {
    if (kommentar.trim().length < 5) {
      toast.error("Bitte eine Begründung (min. 5 Zeichen) angeben.");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/abrechnungen/${settlement.id}/stornieren`, {
        kommentar: kommentar.trim(), neu_erzeugen: neu,
      });
      toast.success("Abrechnung storniert.");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="storno-dialog">
        <DialogHeader>
          <DialogTitle>Abrechnung stornieren</DialogTitle>
          <DialogDescription>
            {settlement.liefernummer} · {settlement.lieferant_name} · {fmtMonat(settlement.monat)} ·
            Brutto {fmtEur(settlement.brutto_eur)}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-lg bg-muted/40 p-3 text-xs space-y-1">
            <p>Storno-Gegenbuchung wird angelegt (audit-sicher).</p>
            <p>Original bleibt unverändert und wird als <strong>storniert</strong> markiert.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="storno-kommentar">Begründung (Pflichtfeld)</Label>
            <Textarea id="storno-kommentar" data-testid="storno-comment-input" rows={3}
              value={kommentar} onChange={(e) => setKommentar(e.target.value)}
              placeholder="z. B. Nachträgliche Fett-Korrektur wegen Labornachprüfung …" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={neu} onChange={(e) => setNeu(e.target.checked)}
              data-testid="storno-neu-checkbox" />
            Anschließend neuen Entwurf erzeugen (Standardfall)
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button variant="destructive" onClick={stornieren} disabled={saving} data-testid="storno-submit-button">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Ban className="h-4 w-4 mr-2" />}
            Stornieren
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function AdminAbrechnungslaufPage() {
  const [monat, setMonat] = useState(vormonat());
  const [rows, setRows] = useState([]);
  const [kontrolle, setKontrolle] = useState(null);
  const [milchsorten, setMilchsorten] = useState([]);
  const [gruppen, setGruppen] = useState([]);
  const [umfang, setUmfang] = useState("gesamt");
  const [msFilter, setMsFilter] = useState("");
  const [grFilter, setGrFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [detail, setDetail] = useState(null);
  const [storno, setStorno] = useState(null);
  const [vierAugen, setVierAugen] = useState(false);
  const [selected, setSelected] = useState(new Set());

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [abr, k, ms, gr, sy] = await Promise.all([
        api.get("/abrechnungen", { params: { monat } }),
        api.get("/abrechnung/kontrolle", { params: { monat } }),
        api.get("/milchsorten"),
        api.get("/gruppen"),
        api.get("/einstellungen/system"),
      ]);
      setRows(abr.data);
      setKontrolle(k.data);
      setMilchsorten(ms.data);
      setGruppen(gr.data);
      setVierAugen(!!sy.data.vier_augen_aktiv);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [monat]);

  useEffect(() => { loadAll(); setSelected(new Set()); }, [loadAll]);

  const lauf = async () => {
    setRunning(true);
    try {
      const payload = { monat, umfang };
      if (umfang === "milchsorte") payload.milchsorte_id = msFilter;
      if (umfang === "gruppe") payload.gruppe_id = grFilter;
      const res = await api.post("/abrechnung/lauf", payload);
      toast.success(
        `Abrechnungslauf: ${res.data.erstellt} neu/aktualisiert, ${res.data.uebersprungen} ohne Lieferungen, ${res.data.geschuetzt || 0} geschützt (freigegeben).`
      );
      loadAll();
    } catch (err) {
      toast.error(errMsg(err, "Abrechnungslauf fehlgeschlagen."));
    } finally {
      setRunning(false);
    }
  };

  const freigeben = async (idsOverride) => {
    const ids = idsOverride || Array.from(selected);
    if (!ids.length) {
      toast.error("Bitte mindestens eine Abrechnung auswählen.");
      return;
    }
    try {
      const res = await api.post("/abrechnungen/freigeben", { settlement_ids: ids });
      const msg = vierAugen
        ? `${res.data.freigegeben.length} endgültig freigegeben · ${res.data.wartend_auf_zweitfreigabe.length} wartet auf Zweitfreigabe.`
        : `${res.data.freigegeben.length} Abrechnungen freigegeben.`;
      toast.success(msg);
      setSelected(new Set());
      loadAll();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const freigebenAlle = async () => {
    try {
      const res = await api.post("/abrechnungen/freigeben", { monat });
      toast.success(`${res.data.freigegeben.length} freigegeben.`);
      loadAll();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const loeschen = async (r) => {
    if (!window.confirm("Abrechnungsentwurf wirklich löschen? Historische Daten bleiben erhalten.")) return;
    try {
      await api.delete(`/abrechnungen/${r.id}`);
      toast.success("Entwurf gelöscht.");
      loadAll();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const entwuerfe = rows.filter((r) => r.status === "entwurf");
  const toggle = (id) => setSelected((s) => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const toggleAll = () => {
    if (selected.size === entwuerfe.length) setSelected(new Set());
    else setSelected(new Set(entwuerfe.map((r) => r.id)));
  };

  const summe_freigegeben = rows
    .filter((r) => r.status === "freigegeben")
    .reduce((a, r) => a + (r.brutto_eur || 0), 0);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Abrechnungslauf</h1>
        <p className="text-sm text-muted-foreground">
          Milchgeld für einen Monat berechnen, freigeben und stornieren –
          historisierte Stammdaten und Preismodell-Revisionen zum Abrechnungsmonat.
        </p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Lauf konfigurieren</CardTitle>
          <CardDescription>
            Entwurf für gesamt, eine Milchsorte oder eine Gruppe erzeugen.
            Bereits freigegebene Abrechnungen bleiben unberührt.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5">
            <Label>Monat</Label>
            <MonthPicker value={monat} onChange={setMonat} testId="settlement-run-month-select" />
          </div>
          <div className="space-y-1.5">
            <Label>Umfang</Label>
            <Select value={umfang} onValueChange={setUmfang}>
              <SelectTrigger className="w-[180px]" data-testid="settlement-run-scope-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="gesamt">Gesamt</SelectItem>
                <SelectItem value="milchsorte">Nach Milchsorte</SelectItem>
                <SelectItem value="gruppe">Nach Gruppe</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {umfang === "milchsorte" && (
            <div className="space-y-1.5">
              <Label>Milchsorte</Label>
              <Select value={msFilter} onValueChange={setMsFilter}>
                <SelectTrigger className="w-[220px]" data-testid="settlement-run-milchsorte-select">
                  <SelectValue placeholder="Wählen …" />
                </SelectTrigger>
                <SelectContent>
                  {milchsorten.map((m) => (
                    <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {umfang === "gruppe" && (
            <div className="space-y-1.5">
              <Label>Gruppe</Label>
              <Select value={grFilter} onValueChange={setGrFilter}>
                <SelectTrigger className="w-[220px]" data-testid="settlement-run-gruppe-select">
                  <SelectValue placeholder="Wählen …" />
                </SelectTrigger>
                <SelectContent>
                  {gruppen.map((g) => (
                    <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button data-testid="settlement-run-start-button" onClick={lauf} disabled={running}>
            {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Calculator className="h-4 w-4 mr-2" />}
            Abrechnungslauf starten
          </Button>
        </CardContent>
      </Card>

      <Tabs defaultValue="ergebnis">
        <TabsList>
          <TabsTrigger value="ergebnis" data-testid="tab-result">Ergebnis ({rows.length})</TabsTrigger>
          <TabsTrigger value="kontrolle" data-testid="tab-control">
            Kontrolle (fehlend: {kontrolle?.anzahl_fehlend ?? 0})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="ergebnis" className="mt-4">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-between gap-3">
                <div className="min-w-0">
                  <CardTitle className="text-base">Abrechnungen {fmtMonat(monat)}</CardTitle>
                  <CardDescription>
                    Entwürfe: {kontrolle?.anzahl_entwurf ?? 0} · Freigegeben: {kontrolle?.anzahl_freigegeben ?? 0} · Storniert: {kontrolle?.anzahl_storniert ?? 0} · Summe Freigegeben {fmtEur(summe_freigegeben)}
                    {vierAugen && <Badge variant="outline" className="ml-2">4-Augen aktiv</Badge>}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button variant="outline" size="sm" onClick={() => freigeben()} disabled={selected.size === 0}
                    data-testid="release-selected-button">
                    <CheckCircle2 className="h-4 w-4 mr-1" /> Auswahl freigeben ({selected.size})
                  </Button>
                  <Button size="sm" onClick={freigebenAlle} disabled={entwuerfe.length === 0}
                    data-testid="release-all-button">
                    <CheckCircle2 className="h-4 w-4 mr-1" /> Alle Entwürfe freigeben
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
              ) : rows.length ? (
                <div className="overflow-x-auto">
                  <Table data-testid="settlement-run-results-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10">
                          <input type="checkbox" data-testid="select-all-checkbox"
                            checked={entwuerfe.length > 0 && selected.size === entwuerfe.length}
                            onChange={toggleAll} />
                        </TableHead>
                        <TableHead>Liefernr.</TableHead>
                        <TableHead>Lieferant</TableHead>
                        <TableHead>Sorte</TableHead>
                        <TableHead className="text-right">Menge</TableHead>
                        <TableHead className="text-right">ct/kg</TableHead>
                        <TableHead>Qualität</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Brutto</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows.map((r) => (
                        <TableRow key={r.id} className={r.status === "storniert" ? "opacity-60" : ""}>
                          <TableCell>
                            {r.status === "entwurf" && (
                              <input type="checkbox" data-testid={`select-${r.liefernummer}`}
                                checked={selected.has(r.id)} onChange={() => toggle(r.id)} />
                            )}
                          </TableCell>
                          <TableCell className="font-num font-medium">{r.liefernummer}</TableCell>
                          <TableCell className="text-sm">{r.lieferant_name}<span className="text-muted-foreground"> · v{r.version}</span></TableCell>
                          <TableCell className="text-sm">{r.milchsorte_name}</TableCell>
                          <TableCell className="text-right font-num">{fmtNum(r.menge_kg)}</TableCell>
                          <TableCell className="text-right font-num">{fmtNum(r.cent_je_kg, 2)}</TableCell>
                          <TableCell><QualityBadge klasse={r.qualitaetsklasse} icon={r.qualitaetsklasse_icon} farbe={r.qualitaetsklasse_farbe} /></TableCell>
                          <TableCell><StatusBadge status={r.status} /></TableCell>
                          <TableCell className="text-right font-num font-semibold">{fmtEur(r.brutto_eur)}</TableCell>
                          <TableCell>
                            <div className="flex gap-0.5 justify-end">
                              <Button variant="ghost" size="icon" onClick={() => setDetail(r)}><Eye className="h-4 w-4" /></Button>
                              {r.status === "entwurf" && (
                                <>
                                  <Button variant="ghost" size="icon" onClick={() => freigeben([r.id])}
                                    data-testid={`release-${r.liefernummer}`}>
                                    <CheckCircle2 className="h-4 w-4 text-[hsl(var(--success))]" />
                                  </Button>
                                  <Button variant="ghost" size="icon" onClick={() => loeschen(r)}>
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                  </Button>
                                </>
                              )}
                              {r.status === "freigegeben" && (
                                <Button variant="ghost" size="icon" onClick={() => setStorno(r)}
                                  data-testid={`storno-${r.liefernummer}`}>
                                  <Ban className="h-4 w-4 text-destructive" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <EmptyState icon={Calculator} title={`Keine Abrechnungen für ${fmtMonat(monat)}.`}
                  text="Starten Sie den Abrechnungslauf für diesen Monat." />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="kontrolle" className="mt-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <ClipboardList className="h-4 w-4 text-primary" /> Kontrolle · {kontrolle?.gesamt_lieferanten ?? 0} aktive Lieferanten
              </CardTitle>
              <CardDescription>Für welche Lieferanten fehlt in {fmtMonat(monat)} noch eine Abrechnung?</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="rounded-lg border p-3">
                    <p className="font-medium flex items-center gap-1 mb-2">
                      <AlertCircle className="h-4 w-4 text-destructive" />
                      Fehlend ({kontrolle?.anzahl_fehlend ?? 0})
                    </p>
                    {kontrolle?.fehlend?.length ? (
                      <ul className="text-xs space-y-1 max-h-80 overflow-y-auto" data-testid="kontrolle-fehlend">
                        {kontrolle.fehlend.map((f) => (
                          <li key={f.liefernummer} className="flex justify-between">
                            <span className="font-num">{f.liefernummer}</span>
                            <span className="text-muted-foreground truncate ml-2">{f.name}</span>
                          </li>
                        ))}
                      </ul>
                    ) : <p className="text-xs text-muted-foreground">Für alle Lieferanten liegen Abrechnungen vor.</p>}
                  </div>
                  <div className="rounded-lg border p-3">
                    <p className="font-medium mb-2">Status-Übersicht</p>
                    <ul className="text-sm space-y-1">
                      <li className="flex justify-between"><span>Entwurf</span><span className="font-num">{kontrolle?.anzahl_entwurf ?? 0}</span></li>
                      <li className="flex justify-between"><span>Freigegeben</span><span className="font-num">{kontrolle?.anzahl_freigegeben ?? 0}</span></li>
                      <li className="flex justify-between"><span>Storniert</span><span className="font-num">{kontrolle?.anzahl_storniert ?? 0}</span></li>
                    </ul>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <AbrechnungDetailDialog abrechnung={detail} onClose={() => setDetail(null)} />
      <StornoDialog settlement={storno} onClose={() => setStorno(null)} onSaved={loadAll} />
    </div>
  );
}
